import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LEVEL_LABELS } from "@/lib/level-badge";
import { STAGE_ACCENTS, stageAccentIndex } from "@/lib/stage-accent";
import { cn } from "@/lib/utils";
import type { Asset, AssetType, ValueChainStage } from "@/lib/assets";

interface AssetListProps {
  assets: Asset[];
  assetTypes: AssetType[];
  stages: ValueChainStage[];
  onEdit: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
}

function formatGps(asset: Asset): string {
  if (!asset.latitude || !asset.longitude) return "—";
  return `${Number(asset.latitude).toFixed(4)}, ${Number(asset.longitude).toFixed(4)}`;
}

/**
 * Read-only 1–4 scale. The redesign draws this as a segmented control, but
 * importance is still edited in AssetForm, so it renders as a display scale
 * (no button semantics, no hover) rather than a control that ignores clicks.
 */
function ImportanceScale({ value }: { value: 1 | 2 | 3 | 4 }) {
  return (
    <div
      className="inline-flex rounded-lg border border-border bg-background p-0.5"
      title={`${LEVEL_LABELS[value]} (${value} of 4)`}
    >
      {([1, 2, 3, 4] as const).map((level) => (
        <span
          key={level}
          aria-hidden
          className={cn(
            "w-7 rounded-sm py-1 text-center text-xs font-semibold",
            level === value ? "bg-primary font-bold text-primary-foreground shadow-sm" : "text-muted-foreground"
          )}
        >
          {level}
        </span>
      ))}
      <span className="sr-only">
        Business importance: {LEVEL_LABELS[value]} ({value} of 4)
      </span>
    </div>
  );
}

function AssetList({ assets, assetTypes, stages, onEdit, onDelete }: AssetListProps) {
  if (assets.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1 py-10 text-center">
        <p className="text-sm text-muted-foreground">No sites yet</p>
        <p className="text-sm text-muted-foreground/70">Add your first site manually or upload an Excel file.</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-background/80 hover:bg-background/80">
          <TableHead>Asset</TableHead>
          <TableHead>Asset type</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>GPS</TableHead>
          <TableHead>Value chain stage</TableHead>
          <TableHead className="text-center">Importance</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {assets.map((asset) => {
          const typeName = asset.assetTypeId
            ? assetTypes.find((t) => t.id === asset.assetTypeId)?.name ?? "—"
            : asset.customAssetTypeName ?? "—";
          const stage = stages.find((s) => s.id === asset.valueChainStageId) ?? null;
          const accent = STAGE_ACCENTS[stageAccentIndex(stage?.id ?? null, stages)];

          return (
            <TableRow key={asset.id}>
              <TableCell>
                <span className="flex items-center gap-2">
                  <span className={cn("size-1.5 shrink-0 rounded-full", accent.dot)} />
                  <span className="font-bold">{asset.name}</span>
                </span>
              </TableCell>
              <TableCell>{typeName}</TableCell>
              <TableCell>
                {asset.city}, {asset.country}
              </TableCell>
              <TableCell className="font-mono text-xs">{formatGps(asset)}</TableCell>
              <TableCell>
                {stage ? (
                  <span className={cn("inline-flex rounded-md border px-2.5 py-1 text-xs", accent.badge)}>
                    {stage.name}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">Not mapped</span>
                )}
              </TableCell>
              <TableCell className="text-center">
                <ImportanceScale value={asset.businessImportance} />
              </TableCell>
              <TableCell>
                <span className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => onEdit(asset)}>
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onDelete(asset)}>
                    Delete
                  </Button>
                </span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default AssetList;
